import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import CommandPalette, { type PaletteItem } from "./command-palette";
import { buildCommandGroups, searchRemote } from "./command-data";
import SettingsChannels from "./settings-channels";
import SettingsNotifications from "./settings-notifications";
import SettingsFeedback from "./settings-feedback";
import SettingsAudit from "./settings-audit";
import { ROLE_LABELS, useAuth } from "./auth";
import { FEEDBACK_JOURNAL_EVENT, journalRequested, requestFeedbackJournal } from "./feedback-store";
import NotificationsPanel from "./notifications-panel";
import useNotifications, { PREFS_EVENT } from "./use-notifications";
import { goAnchor } from "./app-nav";
import { useWorkspaceSettings } from "./workspace-settings";
import { AppsPane, BasicPane, FieldsPane, GeneralPane, IntegrationsPane, RolesPane, TeamPane, ZohoPane } from "./settings-panes";
import { settingsHints, settingsSections } from "@/mocks/settings";
import type { AppNotification, NotifyPrefs } from "./notifications-meta";
import { api, apiErrorText } from "@/lib/api";
import { t } from "@/lib/i18n";

/* Живой макет окна «Настройки»: слева разделы настроек рабочего пространства,
   справа — содержимое выбранного раздела. По умолчанию открыты «Каналы» с пустым состоянием,
   как в оригинале: поиск, кнопка «Добавить канал» и окно добавления. */

const RAIL = [
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings", active: true },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows", active: false },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports", active: false },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts", active: false },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app", active: false },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app", active: false },
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard", active: false },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каталог каналов"), anchor: "#channels", active: false },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents", active: false },
  { id: "ai-assist", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist", active: false },
];

function SettingsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  // если окно открыли по просьбе плашки или уведомления о замечании — сразу журнал
  const [tab, setTab] = useState(() => (journalRequested() ? "feedback" : "channels"));
  const [toast, setToast] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  // плашка «Включить уведомления браузера» — пока браузер не спрашивал разрешение
  const [notifPrompt, setNotifPrompt] = useState(() => typeof Notification !== "undefined" && Notification.permission === "default");
  const notif = useNotifications();
  const { user } = useAuth();
  const ws = useWorkspaceSettings();
  // журнал действий — только владельцу и администраторам
  const sections = settingsSections.filter((s) => s.id !== "audit" || user?.can.audit);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const groups = useMemo(() => buildCommandGroups(), []);

  useEffect(() => () => clearTimeout(timer.current), []);

  // плашка обратной связи просит открыть журнал замечаний
  useEffect(() => {
    const onOpen = () => {
      setTab("feedback");
      const el = document.querySelector("#settings");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    window.addEventListener(FEEDBACK_JOURNAL_EVENT, onOpen);
    return () => window.removeEventListener(FEEDBACK_JOURNAL_EVENT, onOpen);
  }, []);

  // клик мимо панели уведомлений закрывает её (сам колокольчик — внутри [data-notif-root])
  useEffect(() => {
    if (!notifOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-notif-root]")) return;
      setNotifOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [notifOpen]);

  // ⌘K / Ctrl+K открывает поиск, как в оригинальном окне
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
        return;
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const active = sections.find((s) => s.id === tab) ?? settingsSections[6];

  const jump = (anchor: string, label: string) => {
    // в рабочем пространстве раздел открывается целиком, на лендинге — скроллом
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» пока не открывается", { label }));
  };

  // выбор в поиске: раздел настроек открывается здесь же, страница — скроллом, команда — подсказкой
  const onPick = (item: PaletteItem) => {
    if (item.tab) {
      setTab(item.tab);
      flash(t("Открыт раздел «{label}»", { label: item.label }));
      return;
    }
    if (item.anchor) {
      jump(item.anchor, item.label);
      return;
    }
    flash(t("«{label}» появится в следующих этапах", { label: item.label }));
  };

  // клик по уведомлению: ссылка на файл скачивает его, остальные ведут в своё окно или раздел настроек
  const openNotification = (n: AppNotification) => {
    setNotifOpen(false);
    if (n.href?.startsWith("/api/")) {
      window.location.href = n.href;
      return;
    }
    if (n.entity === "feedback" && n.entityId) {
      requestFeedbackJournal(Number(n.entityId));
      return;
    }
    if (n.tab) {
      setTab(n.tab);
      flash(t("Открыт раздел «{label}»", { label: sections.find((x) => x.id === n.tab)?.label ?? n.title }));
      return;
    }
    if (n.href) {
      jump(n.href, n.title);
      return;
    }
    flash(n.title);
  };

  // разрешение браузера: если браузер его не дал — честно говорим об этом; если дал — включаем канал «в браузере» в настройках
  const enableBrowser = async () => {
    let granted = false;
    try {
      if (typeof Notification !== "undefined" && Notification.permission !== "denied") {
        granted = (await Notification.requestPermission()) === "granted";
      }
    } catch {
      granted = false;
    }
    setNotifPrompt(false);
    if (granted) {
      try {
        const p = await api<NotifyPrefs>("/notifications/prefs");
        const saved = await api<NotifyPrefs>("/notifications/prefs", { method: "PUT", json: { channels: { ...p.channels, browser: true } } });
        window.dispatchEvent(new CustomEvent(PREFS_EVENT, { detail: saved }));
      } catch (err) {
        flash(apiErrorText(err));
        return;
      }
    }
    flash(granted ? t("Уведомления браузера включены") : t("Браузер не дал разрешение — включите его в настройках сайта"));
  };

  const sendTest = async () => {
    try {
      const created = await notif.sendTest();
      flash(created ? t("Тестовое уведомление отправлено — смотрите колокольчик в шапке окна") : t("Тест не пришёл: у вас выключены уведомления «Команда и замечания»"));
    } catch (err) {
      flash(apiErrorText(err));
    }
  };

  return (
    <Band id="settings" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки рабочего пространства")} → {active.label.toLowerCase()}
          </span>
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label={t("Открыть поиск по рабочему пространству")}
            className="ms-auto inline-flex h-7 min-w-0 cursor-pointer items-center gap-2 rounded-btn border border-border bg-background px-2.5 text-[11px] text-muted-foreground transition hover:border-primary/40 hover:text-foreground"
          >
            <i className="ri-search-line flex-none text-[13px] leading-none" aria-hidden />
            <span className="hidden truncate sm:inline">{t("Поиск по пространству")}</span>
            <kbd className="hidden flex-none rounded border border-border bg-muted/60 px-1 text-[9.5px] font-semibold leading-[1.4] text-muted-foreground md:inline-flex">⌘K</kbd>
          </button>
          <span className="hidden items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground sm:inline-flex">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {user ? `${t(ROLE_LABELS[user.role] ?? user.role)}: ${user.name}` : ""}
          </span>

          {/* уведомления: колокольчик с бейджем и выпадающая панель */}
          <div className="relative" data-notif-root>
            <button
              type="button"
              onClick={() => setNotifOpen((v) => !v)}
              aria-expanded={notifOpen}
              aria-haspopup="dialog"
              aria-label={notif.unread > 0 ? t("Уведомления, непрочитанных: {n}", { n: notif.unread }) : t("Уведомления")}
              className={cn(
                "relative grid size-7 flex-none cursor-pointer place-items-center rounded-btn border transition",
                notifOpen
                  ? "border-primary/50 bg-primary/10 text-primary"
                  : "border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground",
              )}
            >
              <i className="ri-notification-3-line text-[14px] leading-none" aria-hidden />
              {notif.unread > 0 ? (
                <span className="absolute -end-1.5 -top-1.5 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground ring-2 ring-card">
                  {notif.unread > 9 ? "9+" : notif.unread}
                </span>
              ) : null}
            </button>

            <NotificationsPanel
              open={notifOpen}
              items={notif.items}
              prompt={notifPrompt}
              onClose={() => setNotifOpen(false)}
              onRead={notif.read}
              onArchive={notif.archive}
              onRestore={notif.restore}
              onArchiveAll={() => {
                notif.archiveAll();
                flash(t("Все новые уведомления убраны в архив"));
              }}
              onOpen={openNotification}
              onLater={() => setNotifPrompt(false)}
              onEnableBrowser={enableBrowser}
              onOpenSettings={() => {
                setNotifOpen(false);
                setTab("notifications");
                flash(t("Открыт раздел «Уведомления»"));
              }}
            />
          </div>
        </div>

        <div className="flex">
          {/* иконная панель */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {RAIL.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => jump(r.anchor, r.label)}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.active ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <button
              type="button"
              onClick={() => setNotifOpen((v) => !v)}
              title={notif.unread > 0 ? t("Уведомления: {n}", { n: notif.unread }) : t("Уведомления")}
              aria-label={notif.unread > 0 ? t("Уведомления, непрочитанных: {n}", { n: notif.unread }) : t("Уведомления")}
              className={cn(
                "relative grid size-8 cursor-pointer place-items-center rounded-btn transition",
                notifOpen ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <i className="ri-notification-3-line text-[15px] leading-none" aria-hidden />
              {notif.unread > 0 ? <span className="absolute end-1.5 top-1.5 size-1.5 rounded-full bg-primary ring-2 ring-card" aria-hidden /> : null}
            </button>
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {t("В")}
            </span>
          </div>

          <div className="grid min-w-0 flex-1 lg:grid-cols-[15rem_minmax(0,1fr)]">
            {/* ── разделы настроек ── */}
            <div className="flex flex-col gap-1 border-border p-3 lg:border-e">
              <div className="mb-1 hidden items-center gap-2 px-1.5 lg:flex">
                <span className="grid size-7 flex-none place-items-center rounded-btn bg-foreground/[.07] text-[10px] font-bold text-foreground/70" aria-hidden>
                  {(ws.data?.name || "Hub").slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-semibold leading-tight">{t("Настройки рабочего пространства")}</p>
                  <p className="truncate text-[10px] text-muted-foreground">{ws.data?.name || "Hub"}</p>
                </div>
              </div>

              <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => (s.anchor ? jump(s.anchor, s.label) : setTab(s.id))}
                    aria-pressed={tab === s.id}
                    className={cn(
                      "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                      tab === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={s.icon} aria-hidden />
                    {s.label}
                  </button>
                ))}
              </div>

              <div className="hidden flex-col gap-0.5 lg:flex">
                <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Настройки рабочего пространства")}</p>
                {sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => (s.anchor ? jump(s.anchor, s.label) : setTab(s.id))}
                    aria-current={tab === s.id ? "page" : undefined}
                    className={cn(
                      "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12px] font-medium transition",
                      tab === s.id ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <i className={cn(s.icon, "text-base leading-none")} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{s.label}</span>
                  </button>
                ))}
              </div>

              <div className="mt-3 hidden rounded-btn border border-border bg-background/60 p-3 lg:block">
                <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                  <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                  {t("Доступы")}
                </p>
                <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                  {t("Настройки меняют владелец и администраторы. Агенты видят только свои диалоги.")}
                </p>
              </div>
            </div>

            {/* ── содержимое раздела ── */}
            <div className="min-w-0 border-t border-border lg:border-t-0">
              {tab === "channels" ? <SettingsChannels onFlash={flash} /> : null}
              {tab === "basic" ? <BasicPane onFlash={flash} /> : null}
              {tab === "general" ? <GeneralPane onFlash={flash} /> : null}
              {tab === "roles" ? <RolesPane onFlash={flash} /> : null}
              {tab === "fields" ? <FieldsPane onFlash={flash} /> : null}
              {tab === "team" ? <TeamPane onFlash={flash} /> : null}
              {tab === "apps" ? <AppsPane onFlash={flash} /> : null}
              {tab === "integrations" ? <IntegrationsPane onFlash={flash} /> : null}
              {tab === "zoho" ? <ZohoPane onFlash={flash} /> : null}
              {tab === "notifications" ? <SettingsNotifications onFlash={flash} onTest={sendTest} /> : null}
              {tab === "feedback" ? <SettingsFeedback onFlash={flash} /> : null}
              {tab === "audit" && user?.can.audit ? <SettingsAudit onFlash={flash} /> : null}
            </div>
          </div>
        </div>

        {/* подсказки внизу окна */}
        <div className="grid gap-2 border-t border-border p-3.5 sm:grid-cols-3">
          {settingsHints.map((h) => (
            <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
              <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                {h.title}
              </p>
              <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
            </div>
          ))}
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}
      </div>

      <CommandPalette open={searchOpen} groups={groups} remote={searchRemote} onClose={() => setSearchOpen(false)} onSelect={onPick} />
    </Band>
  );
}

export default SettingsDemo;